import React, { useEffect, useMemo, useState } from "react";
import { BlobProvider, Document, Font, Image, View } from "@react-pdf/renderer";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import Cookies from "universal-cookie";

import FinportLogo from "images/finport-logo-white.jpg";
import PolicyTable from "./components/PolicyTable/PolicyTable";

import {
  guard,
  constructDisplayPercentage,
  constructDisplayPrice,
  constructShortenedFinancialNumber,
  checkObjectExistsAndNotEmpty,
} from "utils/general";

import {
  Col,
  CoverContainer,
  CoverSection,
  CoverText,
  CoverTitle,
  Desc,
  FooterLeft,
  FooterRight,
  Label,
  Page,
  PageTitleContainer,
  PageTitleText,
  PageTitleIndexContainer,
  PageTitleIndexText,
  Row,
  SectionContainer,
  SectionTitle,
  SectionTitleContainer,
  SectionContentContainer,
  Text,
  TableColumnTitle,
  Divider,
} from "./PDFReportDownloadButton.styles";
import cacheImg from "utils/cacheImg";
import Locale from "react-intl-universal";

const ChineseFontStyle = {
  style: {
    fontSize: 10,
    fontFamily: "ChineseFont",
  },
};

const AppendixChineseFontStyle = {
  style: {
    color: "#fff",
    fontSize: 10,
    fontFamily: "ChineseFont",
  },
};

/* Advisee Summary Info */

const constructPersonalInfo = (data = {}) => [
  { label: Locale.get("pdfDownload.name"), value: data.name },
  { label: Locale.get("pdfDownload.occupation"), value: data.occupation },
  { label: Locale.get("pdfDownload.age"), value: data.age },
  {
    label: Locale.get("pdfDownload.dob"),
    value: moment(data.dob).format("YYYY-MM-DD"),
  },
  {
    label: Locale.get("pdfDownload.youngestDependentAge"),
    value: data.youngestDependentAge,
  },
  {
    label: Locale.get("pdfDownload.yearsResponsible"),
    value: `${data.minYearsOfDependency} ${Locale.get("pdfDownload.years")}`,
  },
  {
    label: Locale.get("pdfDownload.smokerStatus"),
    value: `${
      data.isSmoker
        ? Locale.get("pdfDownload.smokerYes")
        : Locale.get("pdfDownload.smokerNo")
    }`,
  },
  {
    label: Locale.get("pdfDownload.remarks"),
    value: `${data.remarks || "-"}`,
  },
];

const constructCoveredForPersonalInfo = (data = {}) => [
  { label: Locale.get("pdfDownload.name"), value: data.name },
];

const constructFinancialInfo = (data = {}) => [
  {
    label: Locale.get("pdfDownload.monthlySalary"),
    value: constructDisplayPrice(data.monthlyIncomeAmt, "$"),
  },
  {
    label: Locale.get("pdfDownload.monthlyExpense"),
    value: constructDisplayPrice(data.monthlyExpensesAmt, "$"),
    desc: Locale.get("pdfDownload.monthlyExpenseDesc"),
  },
  {
    label: Locale.get("pdfDownload.bMonthlyExpense"),
    value: constructDisplayPrice(data.basicMonthlyExpensesAmt, "$"),
    desc: Locale.get("pdfDownload.bMonthlyExpenseDesc"),
  },
  {
    label: Locale.get("pdfDownload.unsettledMonthlyExpense"),
    value: constructDisplayPrice(data.unsettledMonthlyExpensesAmt, "$"),
    desc: Locale.get("pdfDownload.unsettledMonthlyExpenseDesc"),
  },
  {
    label: Locale.get("pdfDownload.tMonthlyExpense"),
    value: constructDisplayPrice(data.totalMonthlyExpensesAmt, "$"),
    desc: Locale.get("pdfDownload.tMonthlyExpenseDesc"),
  },
  {
    label: Locale.get("pdfDownload.homeOfficeMortgageWithoutMrta"),
    value: constructDisplayPrice(data.uncoveredOwnMortgageLoanBalanceAmt, "$"),
  },
  {
    label: Locale.get("pdfDownload.otherPropertyMortgageWithoutMrta"),
    value: constructDisplayPrice(
      data.uncoveredInvestmentMortgageLoanBalanceAmt,
      "$"
    ),
  },
  {
    label: Locale.get("pdfDownload.personalLoanCreditCard"),
    value: constructDisplayPrice(data.uncoveredPersonalLoanBalanceAmt, "$"),
  },
  {
    label: Locale.get("pdfDownload.immediateDeathExpense"),
    value: constructDisplayPrice(data.immediateDeathExpensesAmt, "$"),
  },
  {
    label: Locale.get("pdfDownload.minYearResponsible"),
    value: `${data.yearsOfDependency} ${Locale.get("pdfDownload.years")}`,
    desc: Locale.get("pdfDownload.minYearResponsibleDesc"),
  },
];

const constructInsuranceInfo = (data = {}) => [
  {
    label: (
      <Text {...ChineseFontStyle}>
        {Locale.get("pdfDownload.deathBenefit")}
      </Text>
    ),
    value: constructDisplayPrice(data.deathBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.tpdb"),
    value: constructDisplayPrice(data.tpdBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.oldAgeDisBenefit"),
    value: constructDisplayPrice(data.oldAgeDisabilityBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.personalAccident"),
    value: constructDisplayPrice(data.personalAccidentBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.totalEarlyCi"),
    value: constructDisplayPrice(data.totalEarlyCIBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.criticalIllness"),
    value: constructDisplayPrice(data.ciBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.weeklyDisabilityIncome"),
    value: constructDisplayPrice(data.accidentalWeeklyIncomeBenefit, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.monthlyDisabilityIncome"),
    value: constructDisplayPrice(data.permanentDisabilityMonthlyIncome, "$"),
    category: "planCoverage",
  },
  {
    label: Locale.get("pdfDownload.dailyBenefit"),
    value: constructDisplayPrice(data.dailyBenefit, "$"),
    category: "hospitalization",
  },
  {
    label: Locale.get("pdfDownload.rab"),
    value: constructDisplayPrice(data.medicalCardRoomAndBoardAmt, "$"),
    category: "medicalCard",
  },
  {
    label: Locale.get("pdfDownload.lifetimeLimit"),
    value:
      data.medicalCardLifetimeLimitAmt >= 0
        ? constructDisplayPrice(data.medicalCardLifetimeLimitAmt, "$")
        : Locale.get("pdfDownload.unlimited"),
    category: "medicalCard",
  },
  {
    label: Locale.get("pdfDownload.yearlyLimit"),
    value: constructDisplayPrice(data.medicalCardYearlyLimitAmt, "$"),
    category: "medicalCard",
  },
  {
    label: Locale.get("pdfDownload.monthlyPremiumPay"),
    value: constructDisplayPrice(data.monthlyPremium, "$"),
  },
];

/* Gross Insurance Needs */

const constructAssumptionInfo = (data = {}) => [
  {
    label: Locale.get("pdfDownload.inflation"),
    value: constructDisplayPercentage(data.inflation),
  },
  {
    label: Locale.get("pdfDownload.interestRate"),
    value: constructDisplayPercentage(data.investmentInterestRate),
  },
  {
    label: Locale.get("pdfDownload.estAdjustedInflation"),
    value: constructDisplayPercentage(data.estimateAdjInflation),
  },
];

const constructGrossInsNeedsDataSource = (title, data = {}) => {
  const dataSource = [
    {
      label: `${title} ${Locale.get("pdfDownload.For")} ${
        data.yearsOfCoverage
      } ${Locale.get("pdfDownload.years")}`,
      value: constructShortenedFinancialNumber(data.lifeInsNeeds, "$"),
    },
    {
      label: Locale.get("pdfDownload.monthlyExpenditure"),
      value: constructDisplayPrice(data.monthlyExpenditure, "$"),
    },
    {
      label: Locale.get("pdfDownload.annualExpenditure"),
      value: constructDisplayPrice(data.annualExpenditure, "$"),
    },
    {
      label: Locale.get("pdfDownload.yearCoverageBasicResponsible"),
      value: `${data.yearsOfCoverage} ${Locale.get("pdfDownload.years")}`,
    },
  ];

  if (data.ide) {
    dataSource.splice(2, 0, {
      label: Locale.get("pdfDownload.immediateDeathExpenseGrossInsurance"),
      value: constructDisplayPrice(data.ide, "$"),
    });
  }
  if (data.recoverRecuperatingFund) {
    dataSource.push({
      label: Locale.get("pdfDownload.rrf"),
      value: constructDisplayPrice(data.recoverRecuperatingFund, "$"),
    });
  }
  if (data.medicalHealthInsuranceNeeds) {
    dataSource.push({
      label: Locale.get("pdfDownload.medicalHealthInsuranceNeed"),
      value: constructDisplayPrice(data.medicalHealthInsuranceNeeds, "$"),
    });
  }
  return dataSource;
};

/* Policy */

const constructPolicyPlanDescription = (data = {}, personalDetails) => [
  { label: Locale.get("pdfDownload.planName"), value: data.name },
  { label: Locale.get("pdfDownload.policyNo"), value: data.policyNumber },
  { label: Locale.get("pdfDownload.company"), value: data.company },
  {
    label: Locale.get("pdfDownload.commencementDate"),
    value: data.commencementDate,
  },
  { label: Locale.get("pdfDownload.maturityDate"), value: data.maturityDate },
  { label: Locale.get("pdfDownload.planType"), value: data.type },
  {
    label: Locale.get("pdfDownload.premium"),
    value: constructDisplayPrice(data.premium, "$"),
  },
  {
    label: Locale.get("pdfDownload.yearlyPremium"),
    value: constructDisplayPrice(data.yearlyPremium, "$"),
  },
  { label: Locale.get("pdfDownload.mop"), value: data.paymentMode },
  { label: Locale.get("pdfDownload.status"), value: data.status },
  {
    label: Locale.get("pdfDownload.lifeAssured"),
    value: data.coveredFor
      ? data.coveredFor
      : data.linkedFamily
      ? data.linkedFamily
      : `${personalDetails.name.firstName} ${personalDetails.name.lastName}`,
  },
];

const constructPolicyGuaranteedCashPayment = (data = {}) =>
  [
    { label: Locale.get("pdfDownload.startDate"), value: data.startingDate },
    { label: Locale.get("pdfDownload.amount"), value: data.amount },
    {
      label: Locale.get("pdfDownload.estFund20year"),
      value:
        data.fundsAt20YearsAmt &&
        constructDisplayPrice(data.fundsAt20YearsAmt, "$"),
    },
    {
      label: Locale.get("pdfDownload.fundsFinalMaturity"),
      value:
        guard(() => data.fundsMaturity.age, 0) +
        " " +
        Locale.get("pdfDownload.yearOld"),
    },
    {
      label: Locale.get("pdfDownload.estFundMaturity"),
      value:
        guard(() => data.fundsMaturity.estimateFunds, 0) &&
        constructDisplayPrice(data.fundsMaturity.estimateFunds, "$"),
    },
  ].filter((obj) => obj.value);

const constructPolicyMedicalCard = (data = {}) =>
  [
    { label: Locale.get("pdfDownload.name"), value: data.name },
    {
      label: Locale.get("pdfDownload.rab"),
      value:
        data.roomAndBoardAmt &&
        constructDisplayPrice(data.roomAndBoardAmt, "$"),
    },
    {
      label: Locale.get("pdfDownload.lifetimeLimit"),
      value:
        data.lifetimeLimitAmt &&
        (data.lifetimeLimitAmt >= 0
          ? constructDisplayPrice(data.lifetimeLimitAmt, "$")
          : Locale.get("pdfDownload.unlimited")),
    },
    {
      label: Locale.get("pdfDownload.yearlyLimit"),
      value:
        data.yearlyLimitAmt && constructDisplayPrice(data.yearlyLimitAmt, "$"),
    },
    {
      label: Locale.get("pdfDownload.outpatientCancerTreat"),
      value:
        data.outpatientCancerTreatmentAmt &&
        constructDisplayPrice(data.outpatientCancerTreatmentAmt, "$"),
    },
    {
      label: Locale.get("pdfDownload.outpatientKidneyDialysisTreat"),
      value:
        data.outpatientKidneyDialysisTreatmentAmt &&
        constructDisplayPrice(data.outpatientKidneyDialysisTreatmentAmt, "$"),
    },
    {
      label: Locale.get("pdfDownload.deductible"),
      value: data.deductible && constructDisplayPrice(data.deductible, "$"),
    },
    { label: Locale.get("pdfDownload.coInsurance"), value: data.coInsurance },
    { label: Locale.get("pdfDownload.remark"), value: data.remarks },
  ].filter((obj) => obj.value);

const constructAplAndPolicyLoan = (data = {}) =>
  [
    {
      label: Locale.get("pdfDownload.apl"),
      value: data.apl && constructDisplayPrice(data.apl, "$"),
    },
    {
      label: Locale.get("pdfDownload.policyLoan"),
      value: data.policyLoan && constructDisplayPrice(data.policyLoan, "$"),
    },
  ].filter((obj) => obj.value);

const constructSurrenderValue = (data = {}) =>
  [
    {
      label: Locale.get("pdfDownload.guaranteedSurrenderValue"),
      value:
        guard(() => data.guaranteedValue.amount) &&
        constructDisplayPrice(data.guaranteedValue.amount, "$"),
    },
    {
      label: Locale.get("pdfDownload.fundValue"),
      value:
        guard(() => data.dividend.amount) &&
        constructDisplayPrice(data.dividend.amount, "$"),
    },
    {
      label: Locale.get("pdfDownload.regularCashPayout"),
      value:
        guard(() => data.regularCashPayout.amount) &&
        constructDisplayPrice(data.regularCashPayout.amount, "$"),
    },
    {
      label: Locale.get("pdfDownload.terminalDiv"),
      value:
        guard(() => data.terminalDividend.amount) &&
        constructDisplayPrice(data.terminalDividend.amount, "$"),
    },
    {
      label: Locale.get("pdfDownload.tSurrenderValue"),
      value: guard(() => data.total) && constructDisplayPrice(data.total, "$"),
    },
  ].filter((obj) => obj.value);

/* Cashflow */

const constructCashFlowSummary = (data = {}) => [
  {
    label: Locale.get("pdfDownload.netCashFlow"),
    value: constructDisplayPrice(data.nettCashFlow, "$"),
  },
  {
    label: Locale.get("pdfDownload.basicLiquidFund"),
    value: constructDisplayPrice(data.liquidityFunds, "$"),
  },
  {
    label: Locale.get("pdfDownload.monthlyIncomeSIndicator"),
    value: constructDisplayPrice(data.incomeStrengthIndicator, "$"),
  },
  {
    label: Locale.get("pdfDownload.tCashInflow"),
    value: `${constructDisplayPrice(data.totalInflow, "$")} ${
      data.isInflowTotalOverridden ? Locale.get("pdfDownload.override") : ""
    }`,
  },
  {
    label: Locale.get("pdfDownload.tCashOutflow"),
    value: `${constructDisplayPrice(data.totalOutflow, "$")} ${
      data.isOutflowTotalOverridden ? Locale.get("pdfDownload.override") : ""
    }`,
  },
];

/* Estate Planning */

/* Investment Portfolio */

const constructInvestmentGoal = (data = {}) => [
  {
    label: Locale.get("pdfDownload.description"),
    value: guard(() => data.description, "Description (TBA)"),
  },
  {
    label: Locale.get("pdfDownload.goalAmount"),
    value: guard(
      () => (data.goalAmt ? constructDisplayPrice(data.goalAmt, "$") : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.yearsToAchieve"),
    value:
      guard(() => data.yearsToAchieve, "-") +
      " " +
      Locale.get("pdfDownload.yearSuffix"),
  },
];

const constructInvestmentAssumption = (data = {}) => [
  {
    label: Locale.get("pdfDownload.inflation"),
    value: guard(
      () => (data.inflation ? constructDisplayPercentage(data.inflation) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.unitTrustRate"),
    value: guard(
      () => (data.unitTrust ? constructDisplayPercentage(data.unitTrust) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.epfRate"),
    value: guard(
      () => (data.epf ? constructDisplayPercentage(data.epf) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.shareRate"),
    value: guard(
      () => (data.share ? constructDisplayPercentage(data.share) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.fdRate"),
    value: guard(
      () => (data.fd ? constructDisplayPercentage(data.fd) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.propertyHouseIndex"),
    value: guard(
      () => (data.property ? constructDisplayPercentage(data.property) : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.salaryIncreaseRate"),
    value: guard(
      () =>
        data.salaryIncrease
          ? constructDisplayPercentage(data.salaryIncrease)
          : "-",
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.bSavingsCurrent"),
    value: guard(
      () =>
        data.bankSavings ? constructDisplayPercentage(data.bankSavings) : "-",
      "-"
    ),
  },
];

const constructInvestmentSummaryInfo = (data = {}) => [
  {
    label: Locale.get("pdfDownload.tProjectedNetValue"),
    value: guard(
      () =>
        data.projectedNet ? constructDisplayPrice(data.projectedNet, "$") : "-",
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.shortage"),
    value: guard(
      () => (data.shortage ? constructDisplayPrice(data.shortage, "$") : "-"),
      "-"
    ),
  },
  {
    label: Locale.get("pdfDownload.availableFunds"),
    value: guard(
      () =>
        data.monthlyAvailableFunds
          ? constructDisplayPrice(data.monthlyAvailableFunds, "$")
          : "-",
      "-"
    ),
  },
];

/* Local helper functions */
const getAdvisorCompanyInfoForDisplay = (personalInfo) => {
  const { advisorInfo } = personalInfo;
  const { company } = advisorInfo || {};

  const companyLogo = guard(() => company.logo.url, FinportLogo);
  const companyName = guard(() => company.name.toUpperCase(), "");

  return { companyName, companyLogo };
};

/* Local Components */

const PageTitle = ({ title, index }) => {
  return (
    <PageTitleContainer>
      <Row>
        <PageTitleIndexContainer>
          <PageTitleIndexText>{index}</PageTitleIndexText>
        </PageTitleIndexContainer>
        <PageTitleText>{title}</PageTitleText>
      </Row>
    </PageTitleContainer>
  );
};

const InfoBox = ({
  title,
  dataSource,
  isSideBySide,
  bordered = true,
  marginBottom,
}) => {
  return (
    <SectionContainer
      wrap={false}
      marginBottom={marginBottom}
      bordered={bordered}
    >
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data) => (
          <Col width={isSideBySide ? "50%" : "100%"} key={data.label}>
            <Row>
              <Label width="45%" font>
                {data.label === "Daily Benefits"
                  ? Locale.get("pdfDownload.dailyBenefits")
                  : data.label}
              </Label>
              <Text {...ChineseFontStyle} width="55%">
                {`:   ${data.value || "-"}`}{" "}
              </Text>
              <Desc>{data.desc}</Desc>
            </Row>
          </Col>
        ))}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const NominationDetailsInfoBox = ({
  title,
  dataSource,
  isSideBySide,
  bordered = true,
  marginBottom,
}) => {
  return (
    <SectionContainer
      wrap={false}
      marginBottom={marginBottom}
      bordered={bordered}
    >
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data, index) => {
          return (
            <Row key={index}>
              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Name</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.name || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityType || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Contact Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.contactNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Benefit Share</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.benefitShare + "%" || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Relationship</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.relationship || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Trust / Non Trust</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.trustNonTrust || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              {index + 1 !== dataSource.length && <Divider />}
            </Row>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const TrusteeDetailsInfoBox = ({
  title,
  dataSource,
  isSideBySide,
  bordered = true,
  marginBottom,
}) => {
  return (
    <SectionContainer
      wrap={false}
      marginBottom={marginBottom}
      bordered={bordered}
    >
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data, index) => {
          return (
            <Row key={index}>
              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Ind / Comp Name</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.name || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Ind / Comp Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.individualCompany || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityType || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Contact Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.contactNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              {index + 1 !== dataSource.length && <Divider />}
            </Row>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const AssigneeDetailsInfoBox = ({
  title,
  dataSource,
  isSideBySide,
  bordered = true,
  marginBottom,
}) => {
  return (
    <SectionContainer
      wrap={false}
      marginBottom={marginBottom}
      bordered={bordered}
    >
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data, index) => {
          return (
            <Row key={index}>
              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Assignee Name</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.assigneeName || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Assign Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.assignType || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Ind / Comp Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.individualCompany || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Cond. / Absolute</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.conditionAbsolute || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Relationship</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.relationship || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              {index + 1 !== dataSource.length && <Divider />}
            </Row>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const ContingentOwnerInfoBox = ({
  title,
  dataSource,
  isSideBySide,
  bordered = true,
  marginBottom,
}) => {
  return (
    <SectionContainer
      wrap={false}
      marginBottom={marginBottom}
      bordered={bordered}
    >
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data, index) => {
          return (
            <Row key={index}>
              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Name</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.name || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Type</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityType || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Identity Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.identityNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Contact Number</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.contactNumber || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              <Col width={isSideBySide ? "50%" : "100%"}>
                <Row>
                  <Label width="45%">Relationship</Label>
                  <Text {...ChineseFontStyle} width="55%">
                    {`:   ${data.relationship || "-"}`}{" "}
                  </Text>
                  {/* <Desc>{data.desc}</Desc> */}
                </Row>
              </Col>

              {index + 1 !== dataSource.length && <Divider />}
            </Row>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const NominationsTable = ({ data }) => {
  const contactColumns = [
    {
      key: "name",
      label: "Name",
      widthInPerc: "20%",
    },
    {
      key: "identityType",
      label: "ID Type",
      widthInPerc: "15%",
    },
    {
      key: "identityNumber",
      label: "ID No.",
      widthInPerc: "15%",
    },
    {
      key: "contactNumber",
      label: "Contact No.",
      widthInPerc: "15%",
    },
    {
      key: "benefitShare",
      label: "Benefit Share",
      widthInPerc: "10%",
    },
    {
      key: "relationship",
      label: "Relationship",
      widthInPerc: "15%",
    },
    {
      key: "trustNonTrust",
      label: "Trust/Non Trust",
      widthInPerc: "10%",
    },
  ];

  const contactSources = [
    {
      role: "Insurance Company",
      nameOrCompany: "Great Eastern Life Assurance (Malaysia) Berhad",
      email: "wecare-my@greateasternlife.com",
      website: "http://www.greateasternlife.com/my/en/personal-insurance.html",
      contact: "NA",
      officeNumber: "1300130088",
      faxNumber: "0342598000(FAX)",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Hong Leong Assurance Berhad",
      email: "customerservice@hla.hongleong.com.my",
      website: "http://www.hla.com.my",
      contact: "0125226892",
      officeNumber: "0376501818",
      faxNumber: "0376501991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AmMetLife Insurance Berhad",
      email: "customercare@ammetlife.com",
      website: "http://www.ammetlife.com",
      contact: "NA",
      officeNumber: "0322718000",
      faxNumber: "0322723230",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA General Berhad",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA Bhd. (AIA)",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Allianz General Insurance Company (Malaysia) Berhad",
      email: "NA",
      website: "http://www.allianz.com.my",
      contact: "NA",
      officeNumber: "1300881028",
      faxNumber: "NA",
    },
    {
      role: "Investment",
      nameOrCompany: "Public Mutual Bhd",
      email: "customer@publicmutual.com.my",
      website: "http://www.publicmutual.com.my",
      contact: "0125059802\n0125886080",
      officeNumber: "052105000",
      faxNumber: "0362779800",
    },
    {
      role: "Investment",
      nameOrCompany: "Affin Hwang Capital",
      email: "enquiry@affinhwang.com",
      website: "http://www.affinhwang.com",
      contact: "NA",
      officeNumber: "0321423700",
      faxNumber: "0321423799",
    },
    {
      role: "Investment",
      nameOrCompany: "Phillip Mutual Bhd",
      email: "pmbpg@poems.com.my",
      website: "http://www.phillipmutual.com",
      contact: "NA",
      officeNumber: "042616866",
      faxNumber: "042614346",
    },
    {
      role: "Executor or Trustee",
      nameOrCompany: "Rockwill Trustee Corporation",
      email: "general@rockwills.com",
      website: "http://www,rockwills.com",
      contact: "0125226892",
      officeNumber: "0377811993",
      faxNumber: "0377812993",
    },
    {
      role: "Government Retirement Scheme",
      nameOrCompany: "Employee Provident Fund (EPF)",
      email: "NA",
      website: "http://www.kwsp.gov.my/portal/ms/web/kwsp/home",
      contact: "NA",
      officeNumber: "0389226000",
      faxNumber: "0389226222",
    },
  ];

  return (
    <>
      {/* <PageTitle
        title={`Emergency Contact`}
        index={'Appendix ' + (policies.length + 1)}
      /> */}
      {/* {contactSources.map((value, index) => (
        <InfoBox
          key={index}
          title={`Role : ${value.role}`}
          dataSource={[
            {
              label: "Name or Company",
              value: value.nameOrCompany
            },
            {
              label: "Email",
              value: value.email
            },
            {
              label: "Website",
              value: value.website
            },
            {
              label: "H/P Contact",
              value: value.contact
            },
            {
              label: "Office Number",
              value: value.officeNumber
            },
            {
              label: "Fax Number",
              value: value.faxNumber
            }
          ]}
        />
      ))} */}
      <NominationsTableWithMultipleColumns
        title="Nomination Details"
        columns={contactColumns}
        dataSource={data}
      />
    </>
  );
};

const NominationsTableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={true} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];

                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                      style={{
                        borderBottomColor: "#C6C6C6",
                        borderBottomWidth: 1,
                        paddingBottom: 12,
                        fontSize: 8,
                      }}
                    >
                      {column.key === "benefitShare"
                        ? data.benefitShare + "%"
                        : column.key === "trustNonTrust" &&
                          data.trustNonTrust === "trust"
                        ? "Trust"
                        : column.key === "trustNonTrust" &&
                          data.trustNonTrust === "nonTrust"
                        ? "Non Trust"
                        : dataToDisplay}

                      {/* {dataToDisplay || "-"} */}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const TrusteeTable = ({ data }) => {
  const contactColumns = [
    {
      key: "name",
      label: "Ind/Comp Name",
      widthInPerc: "35%",
    },
    {
      key: "individualCompany",
      label: "Ind/Comp Type",
      widthInPerc: "15%",
    },
    {
      key: "identityType",
      label: "ID Type",
      widthInPerc: "15%",
    },
    {
      key: "identityNumber",
      label: "ID No.",
      widthInPerc: "20%",
    },
    {
      key: "contactNumber",
      label: "Contact No.",
      widthInPerc: "15%",
    },
  ];

  const contactSources = [
    {
      role: "Insurance Company",
      nameOrCompany: "Great Eastern Life Assurance (Malaysia) Berhad",
      email: "wecare-my@greateasternlife.com",
      website: "http://www.greateasternlife.com/my/en/personal-insurance.html",
      contact: "NA",
      officeNumber: "1300130088",
      faxNumber: "0342598000(FAX)",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Hong Leong Assurance Berhad",
      email: "customerservice@hla.hongleong.com.my",
      website: "http://www.hla.com.my",
      contact: "0125226892",
      officeNumber: "0376501818",
      faxNumber: "0376501991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AmMetLife Insurance Berhad",
      email: "customercare@ammetlife.com",
      website: "http://www.ammetlife.com",
      contact: "NA",
      officeNumber: "0322718000",
      faxNumber: "0322723230",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA General Berhad",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA Bhd. (AIA)",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Allianz General Insurance Company (Malaysia) Berhad",
      email: "NA",
      website: "http://www.allianz.com.my",
      contact: "NA",
      officeNumber: "1300881028",
      faxNumber: "NA",
    },
    {
      role: "Investment",
      nameOrCompany: "Public Mutual Bhd",
      email: "customer@publicmutual.com.my",
      website: "http://www.publicmutual.com.my",
      contact: "0125059802\n0125886080",
      officeNumber: "052105000",
      faxNumber: "0362779800",
    },
    {
      role: "Investment",
      nameOrCompany: "Affin Hwang Capital",
      email: "enquiry@affinhwang.com",
      website: "http://www.affinhwang.com",
      contact: "NA",
      officeNumber: "0321423700",
      faxNumber: "0321423799",
    },
    {
      role: "Investment",
      nameOrCompany: "Phillip Mutual Bhd",
      email: "pmbpg@poems.com.my",
      website: "http://www.phillipmutual.com",
      contact: "NA",
      officeNumber: "042616866",
      faxNumber: "042614346",
    },
    {
      role: "Executor or Trustee",
      nameOrCompany: "Rockwill Trustee Corporation",
      email: "general@rockwills.com",
      website: "http://www,rockwills.com",
      contact: "0125226892",
      officeNumber: "0377811993",
      faxNumber: "0377812993",
    },
    {
      role: "Government Retirement Scheme",
      nameOrCompany: "Employee Provident Fund (EPF)",
      email: "NA",
      website: "http://www.kwsp.gov.my/portal/ms/web/kwsp/home",
      contact: "NA",
      officeNumber: "0389226000",
      faxNumber: "0389226222",
    },
  ];

  return (
    <>
      {/* <PageTitle
        title={`Emergency Contact`}
        index={'Appendix ' + (policies.length + 1)}
      /> */}
      {/* {contactSources.map((value, index) => (
        <InfoBox
          key={index}
          title={`Role : ${value.role}`}
          dataSource={[
            {
              label: "Name or Company",
              value: value.nameOrCompany
            },
            {
              label: "Email",
              value: value.email
            },
            {
              label: "Website",
              value: value.website
            },
            {
              label: "H/P Contact",
              value: value.contact
            },
            {
              label: "Office Number",
              value: value.officeNumber
            },
            {
              label: "Fax Number",
              value: value.faxNumber
            }
          ]}
        />
      ))} */}
      <TrusteeTableWithMultipleColumns
        title="Trustee Details"
        columns={contactColumns}
        dataSource={data}
      />
    </>
  );
};

const TrusteeTableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={true} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];

                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                      style={{
                        borderBottomColor: "#C6C6C6",
                        borderBottomWidth: 1,
                        paddingBottom: 12,
                        fontSize: 8,
                      }}
                    >
                      {column.key === "individualCompany" &&
                      data.individualCompany === "individual"
                        ? "Individual"
                        : column.key === "individualCompany" &&
                          data.individualCompany === "company"
                        ? "Company"
                        : dataToDisplay}

                      {/* {dataToDisplay || "-"} */}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const AssigneeTable = ({ data }) => {
  const contactColumns = [
    {
      key: "assigneeName",
      label: "Assignee Name",
      widthInPerc: "25%",
    },
    {
      key: "assignType",
      label: "Assign Type",
      widthInPerc: "20%",
    },
    {
      key: "individualCompany",
      label: "Ind/Comp Type",
      widthInPerc: "15%",
    },
    {
      key: "conditionAbsolute",
      label: "Cond. / Absolute",
      widthInPerc: "20%",
    },
    {
      key: "relationship",
      label: "Relationship",
      widthInPerc: "20%",
    },
  ];

  const contactSources = [
    {
      role: "Insurance Company",
      nameOrCompany: "Great Eastern Life Assurance (Malaysia) Berhad",
      email: "wecare-my@greateasternlife.com",
      website: "http://www.greateasternlife.com/my/en/personal-insurance.html",
      contact: "NA",
      officeNumber: "1300130088",
      faxNumber: "0342598000(FAX)",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Hong Leong Assurance Berhad",
      email: "customerservice@hla.hongleong.com.my",
      website: "http://www.hla.com.my",
      contact: "0125226892",
      officeNumber: "0376501818",
      faxNumber: "0376501991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AmMetLife Insurance Berhad",
      email: "customercare@ammetlife.com",
      website: "http://www.ammetlife.com",
      contact: "NA",
      officeNumber: "0322718000",
      faxNumber: "0322723230",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA General Berhad",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA Bhd. (AIA)",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Allianz General Insurance Company (Malaysia) Berhad",
      email: "NA",
      website: "http://www.allianz.com.my",
      contact: "NA",
      officeNumber: "1300881028",
      faxNumber: "NA",
    },
    {
      role: "Investment",
      nameOrCompany: "Public Mutual Bhd",
      email: "customer@publicmutual.com.my",
      website: "http://www.publicmutual.com.my",
      contact: "0125059802\n0125886080",
      officeNumber: "052105000",
      faxNumber: "0362779800",
    },
    {
      role: "Investment",
      nameOrCompany: "Affin Hwang Capital",
      email: "enquiry@affinhwang.com",
      website: "http://www.affinhwang.com",
      contact: "NA",
      officeNumber: "0321423700",
      faxNumber: "0321423799",
    },
    {
      role: "Investment",
      nameOrCompany: "Phillip Mutual Bhd",
      email: "pmbpg@poems.com.my",
      website: "http://www.phillipmutual.com",
      contact: "NA",
      officeNumber: "042616866",
      faxNumber: "042614346",
    },
    {
      role: "Executor or Trustee",
      nameOrCompany: "Rockwill Trustee Corporation",
      email: "general@rockwills.com",
      website: "http://www,rockwills.com",
      contact: "0125226892",
      officeNumber: "0377811993",
      faxNumber: "0377812993",
    },
    {
      role: "Government Retirement Scheme",
      nameOrCompany: "Employee Provident Fund (EPF)",
      email: "NA",
      website: "http://www.kwsp.gov.my/portal/ms/web/kwsp/home",
      contact: "NA",
      officeNumber: "0389226000",
      faxNumber: "0389226222",
    },
  ];

  return (
    <>
      {/* <PageTitle
        title={`Emergency Contact`}
        index={'Appendix ' + (policies.length + 1)}
      /> */}
      {/* {contactSources.map((value, index) => (
        <InfoBox
          key={index}
          title={`Role : ${value.role}`}
          dataSource={[
            {
              label: "Name or Company",
              value: value.nameOrCompany
            },
            {
              label: "Email",
              value: value.email
            },
            {
              label: "Website",
              value: value.website
            },
            {
              label: "H/P Contact",
              value: value.contact
            },
            {
              label: "Office Number",
              value: value.officeNumber
            },
            {
              label: "Fax Number",
              value: value.faxNumber
            }
          ]}
        />
      ))} */}
      <AssigneeTableWithMultipleColumns
        title="Assignee Details"
        columns={contactColumns}
        dataSource={data}
      />
    </>
  );
};

const AssigneeTableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={true} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];

                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                      style={{
                        borderBottomColor: "#C6C6C6",
                        borderBottomWidth: 1,
                        paddingBottom: 12,
                        fontSize: 8,
                      }}
                    >
                      {column.key === "individualCompany" &&
                      data.individualCompany === "individual"
                        ? "Individual"
                        : column.key === "individualCompany" &&
                          data.individualCompany === "company"
                        ? "Company"
                        : dataToDisplay}

                      {/* {dataToDisplay || "-"} */}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const ContingentTable = ({ data }) => {
  const contactColumns = [
    {
      key: "name",
      label: "Name",
      widthInPerc: "25%",
    },
    {
      key: "identityType",
      label: "ID Type",
      widthInPerc: "15%",
    },
    {
      key: "identityNumber",
      label: "ID No.",
      widthInPerc: "20%",
    },
    {
      key: "contactNumber",
      label: "Contact Number",
      widthInPerc: "20%",
    },
    {
      key: "relationship",
      label: "Relationship",
      widthInPerc: "20%",
    },
  ];

  const contactSources = [
    {
      role: "Insurance Company",
      nameOrCompany: "Great Eastern Life Assurance (Malaysia) Berhad",
      email: "wecare-my@greateasternlife.com",
      website: "http://www.greateasternlife.com/my/en/personal-insurance.html",
      contact: "NA",
      officeNumber: "1300130088",
      faxNumber: "0342598000(FAX)",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Hong Leong Assurance Berhad",
      email: "customerservice@hla.hongleong.com.my",
      website: "http://www.hla.com.my",
      contact: "0125226892",
      officeNumber: "0376501818",
      faxNumber: "0376501991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AmMetLife Insurance Berhad",
      email: "customercare@ammetlife.com",
      website: "http://www.ammetlife.com",
      contact: "NA",
      officeNumber: "0322718000",
      faxNumber: "0322723230",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA General Berhad",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA Bhd. (AIA)",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Allianz General Insurance Company (Malaysia) Berhad",
      email: "NA",
      website: "http://www.allianz.com.my",
      contact: "NA",
      officeNumber: "1300881028",
      faxNumber: "NA",
    },
    {
      role: "Investment",
      nameOrCompany: "Public Mutual Bhd",
      email: "customer@publicmutual.com.my",
      website: "http://www.publicmutual.com.my",
      contact: "0125059802\n0125886080",
      officeNumber: "052105000",
      faxNumber: "0362779800",
    },
    {
      role: "Investment",
      nameOrCompany: "Affin Hwang Capital",
      email: "enquiry@affinhwang.com",
      website: "http://www.affinhwang.com",
      contact: "NA",
      officeNumber: "0321423700",
      faxNumber: "0321423799",
    },
    {
      role: "Investment",
      nameOrCompany: "Phillip Mutual Bhd",
      email: "pmbpg@poems.com.my",
      website: "http://www.phillipmutual.com",
      contact: "NA",
      officeNumber: "042616866",
      faxNumber: "042614346",
    },
    {
      role: "Executor or Trustee",
      nameOrCompany: "Rockwill Trustee Corporation",
      email: "general@rockwills.com",
      website: "http://www,rockwills.com",
      contact: "0125226892",
      officeNumber: "0377811993",
      faxNumber: "0377812993",
    },
    {
      role: "Government Retirement Scheme",
      nameOrCompany: "Employee Provident Fund (EPF)",
      email: "NA",
      website: "http://www.kwsp.gov.my/portal/ms/web/kwsp/home",
      contact: "NA",
      officeNumber: "0389226000",
      faxNumber: "0389226222",
    },
  ];

  return (
    <>
      {/* <PageTitle
        title={`Emergency Contact`}
        index={'Appendix ' + (policies.length + 1)}
      /> */}
      {/* {contactSources.map((value, index) => (
        <InfoBox
          key={index}
          title={`Role : ${value.role}`}
          dataSource={[
            {
              label: "Name or Company",
              value: value.nameOrCompany
            },
            {
              label: "Email",
              value: value.email
            },
            {
              label: "Website",
              value: value.website
            },
            {
              label: "H/P Contact",
              value: value.contact
            },
            {
              label: "Office Number",
              value: value.officeNumber
            },
            {
              label: "Fax Number",
              value: value.faxNumber
            }
          ]}
        />
      ))} */}
      <ContingentTableWithMultipleColumns
        title="Contingent Details"
        columns={contactColumns}
        dataSource={data}
      />
    </>
  );
};

const ContingentTableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={true} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];

                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                      style={{
                        borderBottomColor: "#C6C6C6",
                        borderBottomWidth: 1,
                        paddingBottom: 12,
                        fontSize: 8,
                      }}
                    >
                      {column.key === "individualCompany" &&
                      data.individualCompany === "individual"
                        ? "Individual"
                        : column.key === "individualCompany" &&
                          data.individualCompany === "company"
                        ? "Company"
                        : dataToDisplay}

                      {/* {dataToDisplay || "-"} */}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const Policy = ({ policy, index, personalDetails, contactList }) => {
  const planCoverages = policy.benefits.planCoverages.map((coverage) => ({
    ...coverage,
    value:
      coverage.category === "waiver"
        ? "Yes"
        : constructDisplayPrice(coverage.amount, "$"),

    // label:
    //   planCoverages.label === "Death Benefit"
    //     ? Locale.get('pdfDownload.salary')
    //     : planCoverages.label,
  }));

  // const planCoverages = (item) => ({
  //   label:
  //     item.label === "Death Benefit"
  //       ? Locale.get("pdfDownload.deathBenefit")
  //       : item.label
  //   })

  console.log("planCoverages", planCoverages);

  const hospitalization = policy.benefits.hospitalizationBenefits.map(
    (benefit) => ({
      ...benefit,
      value: constructDisplayPrice(benefit.amount, "$"),
    })
  );
  const investmentFunds = policy.investmentFunds.map((fund) => ({
    ...fund,
    value: constructDisplayPrice(fund.amount, "$"),
  }));
  const aplAndPolicyLoan = {
    apl: guard(() => policy.apl.amount, 0),
    policyLoan: guard(() => policy.policyLoan.amount, 0),
  };

  const nominations = policy.nominations;
  const trustee = policy.trustee;
  const assignee = policy.assignee;
  const contingent = policy.contingent;

  // const nominations = policy.nominations.map((nomination) => ({
  //   label: nomination.name,
  //   value: nomination.type,
  //   desc: nomination.relationship
  // }));

  const planCoverageColumns = [
    {
      key: "label",
      label: Locale.get("pdfDownload.type"),
      widthInPerc: "30%",
    },
    {
      key: "value",
      label: Locale.get("pdfDownload.amount"),
      widthInPerc: "30%",
    },
    {
      key: "remarks",
      label: Locale.get("pdfDownload.remark"),
      widthInPerc: "40%",
    },
  ];
  const { policies = [], cashFlow = {} } = personalDetails;

  return (
    <>
      <PageTitle
        title={`${Locale.get("pdfDownload.insurancePolicy")}: ${policy.name}`}
        index={
          <>
            <Text {...AppendixChineseFontStyle}>
              {Locale.get("pdfDownload.appendix")}
            </Text>
            {index}
          </>
        }
      />
      <InfoBox
        title={Locale.get("pdfDownload.planDescription")}
        dataSource={constructPolicyPlanDescription(policy, personalDetails)}
        marginBottom="-1px"
      />
      {/* {!!planCoverages.length ? (
        <InfoBox
          title="Plan Coverage"
          dataSource={planCoverages}
          marginBottom="-1px"
        />
      ) : null} */}

      {!!planCoverages.length ? (
        <TableWithMultipleColumns
          title={Locale.get("pdfDownload.planCoverage")}
          columns={planCoverageColumns}
          dataSource={planCoverages}
          marginBottom="-1px"
        />
      ) : null}

      {checkObjectExistsAndNotEmpty(policy.yearlyGuaranteedCashPayment) && (
        <InfoBox
          title={Locale.get("pdfDownload.yearlyGuaranteeCashPayment")}
          dataSource={constructPolicyGuaranteedCashPayment(
            policy.yearlyGuaranteedCashPayment
          )}
          marginBottom="-1px"
        />
      )}
      {!!hospitalization.length ? (
        <InfoBox
          title={Locale.get("pdfDownload.hospitalizationBenefit")}
          dataSource={hospitalization}
          marginBottom="-1px"
        />
      ) : null}
      {checkObjectExistsAndNotEmpty(policy.medicalCard) && (
        <InfoBox
          title={Locale.get("pdfDownload.medCard")}
          dataSource={constructPolicyMedicalCard(policy.medicalCard)}
          marginBottom="-1px"
        />
      )}
      {!!investmentFunds.length ? (
        <InfoBox
          title={Locale.get("pdfDownload.investmentPlan")}
          dataSource={investmentFunds}
          marginBottom="-1px"
        />
      ) : null}

      {nominations && !!nominations.length ? (
        <NominationsTable data={nominations} />
      ) : null}
      {trustee && !!trustee.length ? <TrusteeTable data={trustee} /> : null}
      {assignee && !!assignee.length ? <AssigneeTable data={assignee} /> : null}
      {contingent && !!contingent.length ? (
        <ContingentTable data={contingent} />
      ) : null}

      {/* {!!nominations.length ? (
        <NominationDetailsInfoBox
          title="Nomination Details"
          dataSource={nominations}
          marginBottom="-1px"
        />
      ) : null}

      {!!trustee.length ? (
        <TrusteeDetailsInfoBox
          title="Trustee Details"
          dataSource={trustee}
          marginBottom="-1px"
        />
      ) : null}

      {!!assignee.length ? (
        <AssigneeDetailsInfoBox
          title="Assignee Details"
          dataSource={assignee}
          marginBottom="-1px"
        />
      ) : null}

      {!!contingent.length ? (
        <ContingentOwnerInfoBox
          title="Contingent Owner"
          dataSource={contingent}
          marginBottom="-1px"
        />
      ) : null} */}

      {checkObjectExistsAndNotEmpty(aplAndPolicyLoan) && (
        <InfoBox
          title={Locale.get("pdfDownload.aplPolicy")}
          dataSource={constructAplAndPolicyLoan(aplAndPolicyLoan)}
          marginBottom="-1px"
        />
      )}
      {checkObjectExistsAndNotEmpty(policy.surrenderValue) && (
        <InfoBox
          title={Locale.get("pdfDownload.surrenderFundValue")}
          dataSource={constructSurrenderValue(policy.surrenderValue)}
          marginBottom="-1px"
        />
      )}
    </>
  );
};

const EmergencyContact = ({ policies, contactList }) => {
  const contactColumns = [
    {
      key: "role",
      label: Locale.get("pdfDownload.role"),
      widthInPerc: "15%",
    },
    {
      key: "nameOrCompany",
      label: Locale.get("pdfDownload.noc"),
      widthInPerc: "15%",
    },
    {
      key: "email",
      label: Locale.get("pdfDownload.emailWebsite"),
      widthInPerc: "55%",
    },
    {
      key: "contact",
      label: `${Locale.get("pdfDownload.contact")}\n
        ${Locale.get("pdfDownload.officeNo")}\n
        ${Locale.get("pdfDownload.faxNo")}`,
      widthInPerc: "15%",
    },
  ];

  const contactSources = [
    {
      role: "Insurance Company",
      nameOrCompany: "Great Eastern Life Assurance (Malaysia) Berhad",
      email: "wecare-my@greateasternlife.com",
      website: "http://www.greateasternlife.com/my/en/personal-insurance.html",
      contact: "NA",
      officeNumber: "1300130088",
      faxNumber: "0342598000(FAX)",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Hong Leong Assurance Berhad",
      email: "customerservice@hla.hongleong.com.my",
      website: "http://www.hla.com.my",
      contact: "0125226892",
      officeNumber: "0376501818",
      faxNumber: "0376501991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AmMetLife Insurance Berhad",
      email: "customercare@ammetlife.com",
      website: "http://www.ammetlife.com",
      contact: "NA",
      officeNumber: "0322718000",
      faxNumber: "0322723230",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA General Berhad",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "AIA Bhd. (AIA)",
      email: "my.customer@aia.com",
      website: "http://www.aia.com.my",
      contact: "NA",
      officeNumber: "0320561111",
      faxNumber: "0320562991",
    },
    {
      role: "Insurance Company",
      nameOrCompany: "Allianz General Insurance Company (Malaysia) Berhad",
      email: "NA",
      website: "http://www.allianz.com.my",
      contact: "NA",
      officeNumber: "1300881028",
      faxNumber: "NA",
    },
    {
      role: "Investment",
      nameOrCompany: "Public Mutual Bhd",
      email: "customer@publicmutual.com.my",
      website: "http://www.publicmutual.com.my",
      contact: "0125059802\n0125886080",
      officeNumber: "052105000",
      faxNumber: "0362779800",
    },
    {
      role: "Investment",
      nameOrCompany: "Affin Hwang Capital",
      email: "enquiry@affinhwang.com",
      website: "http://www.affinhwang.com",
      contact: "NA",
      officeNumber: "0321423700",
      faxNumber: "0321423799",
    },
    {
      role: "Investment",
      nameOrCompany: "Phillip Mutual Bhd",
      email: "pmbpg@poems.com.my",
      website: "http://www.phillipmutual.com",
      contact: "NA",
      officeNumber: "042616866",
      faxNumber: "042614346",
    },
    {
      role: "Executor or Trustee",
      nameOrCompany: "Rockwill Trustee Corporation",
      email: "general@rockwills.com",
      website: "http://www,rockwills.com",
      contact: "0125226892",
      officeNumber: "0377811993",
      faxNumber: "0377812993",
    },
    {
      role: "Government Retirement Scheme",
      nameOrCompany: "Employee Provident Fund (EPF)",
      email: "NA",
      website: "http://www.kwsp.gov.my/portal/ms/web/kwsp/home",
      contact: "NA",
      officeNumber: "0389226000",
      faxNumber: "0389226222",
    },
  ];

  return (
    <>
      <PageTitle
        title={Locale.get("pdfDownload.emergencyContact")}
        index={
          <>
            <Text {...AppendixChineseFontStyle}>
              {Locale.get("pdfDownload.appendix")}
            </Text>
            {policies.length + 1}
          </>
        }
      />
      {/* {contactSources.map((value, index) => (
        <InfoBox
          key={index}
          title={`Role : ${value.role}`}
          dataSource={[
            {
              label: "Name or Company",
              value: value.nameOrCompany
            },
            {
              label: "Email",
              value: value.email
            },
            {
              label: "Website",
              value: value.website
            },
            {
              label: "H/P Contact",
              value: value.contact
            },
            {
              label: "Office Number",
              value: value.officeNumber
            },
            {
              label: "Fax Number",
              value: value.faxNumber
            }
          ]}
        />
      ))} */}
      <EmergencyContactTableWithMultipleColumns
        title={Locale.get("pdfDownload.contactList")}
        columns={contactColumns}
        dataSource={contactList}
      />
    </>
  );
};

const CashFlow = ({ cashFlow }) => {
  const generalFields = [
    {
      key: "label",
      label: Locale.get("pdfDownload.type"),
      widthInPerc: "34%",
    },
    {
      key: "self",
      label: Locale.get("pdfDownload.self"),
      widthInPerc: "17%",
    },
  ];

  const cashInFlowColumns = [
    ...generalFields,
    {
      key: "spouse",
      label: Locale.get("pdfDownload.spouse"),
      widthInPerc: "22%",
    },
    {
      key: "total",
      label: Locale.get("pdfDownload.total"),
      widthInPerc: "22%",
    },
  ];

  const cashOutFlowColumns = [
    ...generalFields,
    {
      key: "remarks",
      label: Locale.get("pdfDownload.remark"),
      widthInPerc: "44%",
    },
  ];

  const cashOutFlowLoanPaymentColumns = [
    ...generalFields,
    {
      key: "loanBalance",
      label: Locale.get("pdfDownload.loanBalance"),
      widthInPerc: "20%",
    },
    {
      key: "isInsuranceCovered",
      label: Locale.get("pdfDownload.mrta"),
      widthInPerc: "10%",
    },
    {
      key: "remarks",
      label: Locale.get("pdfDownload.remark"),
      widthInPerc: "19%",
    },
  ];

  const formatCashflowItem = (item) => ({
    label:
      item.label === "Salary"
        ? Locale.get("pdfDownload.salary")
        : item.label === "Bonuses"
        ? Locale.get("pdfDownload.bonuses")
        : item.label === "Dividend Payout"
        ? Locale.get("pdfDownload.divPayout")
        : item.label === "Rental Income"
        ? Locale.get("pdfDownload.rentalIncome")
        : item.label === "FD Interest Income"
        ? Locale.get("pdfDownload.fdInterestIncome")
        : item.label === "Others"
        ? Locale.get("pdfDownload.others")
        : item.label === "Mortgage for Own Usage"
        ? Locale.get("pdfDownload.mortgageForOwnUsage")
        : item.label === "Cars Loan"
        ? Locale.get("pdfDownload.carLoan")
        : item.label === "Credit Card Repayment"
        ? Locale.get("pdfDownload.creditCardRepayment")
        : item.label === "Personal Loan Repayment"
        ? Locale.get("pdfDownload.personalLoanRepayment")
        : item.label === "Mortgage For Investment"
        ? Locale.get("pdfDownload.mortgageForInvestment")
        : item.label === "Unit Trust regular contribution"
        ? Locale.get("pdfDownload.unitTrustContribution")
        : item.label === "Child Expenses"
        ? Locale.get("pdfDownload.childExpenses")
        : item.label === "Taxes"
        ? Locale.get("pdfDownload.taxes")
        : item.label === "Investment Property"
        ? Locale.get("pdfDownload.investmentProperty")
        : item.label === "Additional EPF Contribution"
        ? Locale.get("pdfDownload.addEpfContribution")
        : item.label === "Food"
        ? Locale.get("pdfDownload.food")
        : item.label === "Clothing, personal care, dental and medical"
        ? Locale.get("pdfDownload.clothingPersonalCareDentalMedical")
        : item.label === "Car insurance, road tax and maintenance"
        ? Locale.get("pdfDownload.carInsuranceRoadTaxMaintenance")
        : item.label === "Utilities and Household"
        ? Locale.get("pdfDownload.utilityHousehold")
        : item.label === "Contribution to Parents"
        ? Locale.get("pdfDownload.contributionToParent")
        : item.label === "Cleaning Maid"
        ? Locale.get("pdfDownload.cleaningMaid")
        : item.label === "Misc"
        ? Locale.get("pdfDownload.misc")
        : item.label === "Vacation"
        ? Locale.get("pdfDownload.vacation")
        : item.label,

    self: constructDisplayPrice(item.amount.self, null),
    spouse: constructDisplayPrice(item.amount.spouse, null),
    total: constructDisplayPrice(item.amount.self + item.amount.spouse, null),
    remarks: item.remarks,
    type: item.type,
    loanBalance: constructDisplayPrice(item.loanBalance, null),
    isInsuranceCovered: item.isInsuranceCovered ? "Yes" : "No",
  });

  const incomeSources = cashFlow.income.sources.map(formatCashflowItem);
  const fixedPayments = cashFlow.expenses.fixedPayments.map(formatCashflowItem);
  const loanPayments = cashFlow.expenses.loanPayments.map(formatCashflowItem);
  const variablePayments = cashFlow.expenses.variablePayments.map(
    formatCashflowItem
  );

  // console.log("loanPayments", loanPayments);

  // const incomeSourceString = JSON.stringify(incomeSources);
  // // console.log("incomeSourceString",incomeSourceString)

  // const totalStrings = incomeSources.map((item) => ({
  //   ...item,
  //   total: item.total.toString(),
  // }));
  // // console.log("totalStrings",totalStrings)

  // const firstIncomeSource = incomeSources[0]
  // // console.log( firstIncomeSource.label)

  // if (firstIncomeSource.label === "Salary"){
  //   console.log(Locale.get('pdfDownload.salary'));
  // }

  //  console.log("cashFlow",cashFlow)

  return (
    <>
      <TableWithMultipleColumns
        title={Locale.get("pdfDownload.incomeSource")}
        columns={cashInFlowColumns}
        dataSource={incomeSources}
      />
      <TableWithMultipleColumns
        title={Locale.get("pdfDownload.loanPayment")}
        columns={cashOutFlowLoanPaymentColumns}
        dataSource={loanPayments}
      />
      <TableWithMultipleColumns
        title={Locale.get("pdfDownload.fixPayment")}
        columns={cashOutFlowColumns}
        dataSource={fixedPayments}
      />
      <TableWithMultipleColumns
        title={Locale.get("pdfDownload.variablePayment")}
        columns={cashOutFlowColumns}
        dataSource={variablePayments}
      />
    </>
  );
};

const RiskRatio = ({ title, dataSource }) => {
  return (
    <SectionContainer wrap={false} marginBottom="-1px" bordered>
      <SectionTitleContainer>
        <SectionTitle>{title}</SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data) => (
          <Col key={data.label}>
            <Row>
              <Label marginRight="30px">{data.label}</Label>
              <Text
                {...ChineseFontStyle}
                width="20%"
                isInRange={data.isInRange}
              >
                {data.value || "-"}
              </Text>
              <Text {...ChineseFontStyle} width="20%">
                {data.range}
              </Text>
              <Desc>{data.desc}</Desc>
            </Row>
          </Col>
        ))}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const TableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={false} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];
                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                    >
                      {dataToDisplay === "Death Benefit"
                        ? Locale.get("pdfDownload.deathBenefit")
                        : dataToDisplay === "Total Permanent Disability Benefit"
                        ? Locale.get(
                            "pdfDownload.totalPermanentDisabilityBenefit"
                          )
                        : dataToDisplay === "Personal Accident"
                        ? Locale.get("pdfDownload.personalAccident")
                        : dataToDisplay === "TPD Waiver of Premium"
                        ? Locale.get("pdfDownload.tpdWaiverOfPremium")
                        : dataToDisplay === "Critical Illness Waiver of Premium"
                        ? Locale.get(
                            "pdfDownload.criticalIllnessWaiverOfPremium"
                          )
                        : dataToDisplay === "Accidental TPD"
                        ? Locale.get("pdfDownload.accidentalTpd")
                        : dataToDisplay === "Critical Illness"
                        ? Locale.get("pdfDownload.criticalIllness")
                        : dataToDisplay === "Total/Early Critical Illness"
                        ? Locale.get("pdfDownload.totalEarlyCi")
                        : dataToDisplay === "Old Age Disability Benefit"
                        ? Locale.get("pdfDownload.oadb")
                        : dataToDisplay === "Daily Benefits"
                        ? Locale.get("pdfDownload.dailyBenefits")
                        : dataToDisplay || "-"}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const EmergencyContactTableWithMultipleColumns = ({
  title,
  columns = [{ key: "", label: "", widthInPerc: "", textCenter: false }],
  dataSource = [],
}) => {
  if (!dataSource.length) {
    const emptyDataSource = columns.reduce((emptyDataSource, col) => {
      emptyDataSource[col.key] = "-";
      return emptyDataSource;
    }, {});
    dataSource = [emptyDataSource];
  }
  return (
    <SectionContainer wrap={true} marginBottom="-1px" bordered>
      {title ? (
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
      ) : null}
      <SectionContentContainer>
        <Col>
          <Row style={{ borderBottom: 1, borderBottomColor: "#e1e1e1" }}>
            {columns.map((column) => {
              return (
                <TableColumnTitle
                  key={column.key}
                  width={
                    column.widthInPerc ? `${column.widthInPerc}%` : undefined
                  }
                  center={column.textCenter}
                >
                  {column.label || column.key}
                </TableColumnTitle>
              );
            })}
          </Row>
        </Col>
        {dataSource.map((data, index) => {
          return (
            <Col key={index}>
              <Row>
                {columns.map((column) => {
                  const dataToDisplay = data[column.key];

                  return (
                    <Text
                      {...ChineseFontStyle}
                      key={column.key}
                      width={
                        column.widthInPerc
                          ? `${column.widthInPerc}%`
                          : undefined
                      }
                      center={column.textCenter}
                      style={{
                        borderBottomColor: "#C6C6C6",
                        borderBottomWidth: 1,
                        paddingBottom: 12,
                      }}
                    >
                      {column.key === "email"
                        ? data.email && data.website
                          ? data.email + "\n" + data.website
                          : !data.email && data.website
                          ? "-" + "\n" + data.website
                          : data.email && !data.website
                          ? data.email + "\n" + "-"
                          : !data.email && !data.website
                          ? "-" + "\n" + "-"
                          : null
                        : column.key === "contact"
                        ? data.contact && data.officeNumber && data.faxNumber
                          ? data.contact +
                            "\n" +
                            data.officeNumber +
                            "\n" +
                            data.faxNumber
                          : !data.contact && data.officeNumber && data.faxNumber
                          ? "-" +
                            "\n" +
                            data.officeNumber +
                            "\n" +
                            data.faxNumber
                          : data.contact && !data.officeNumber && data.faxNumber
                          ? data.contact + "\n" + "-" + "\n" + data.faxNumber
                          : data.contact && data.officeNumber && !data.faxNumber
                          ? data.contact + "\n" + data.officeNumber + "\n" + "-"
                          : !data.contact &&
                            data.officeNumber &&
                            !data.faxNumber
                          ? "-" + "\n" + data.officeNumber + "\n" + "-"
                          : data.contact &&
                            !data.officeNumber &&
                            !data.faxNumber
                          ? data.contact + "\n" + "-" + "\n" + "-"
                          : !data.contact &&
                            !data.officeNumber &&
                            data.faxNumber
                          ? "-" + "\n" + "-" + "\n" + data.faxNumber
                          : !data.contact &&
                            !data.officeNumber &&
                            !data.faxNumber
                          ? "-" + "\n" + "-" + "\n" + "-"
                          : null
                        : dataToDisplay}

                      {/* {dataToDisplay || "-"} */}
                    </Text>
                  );
                })}
              </Row>
            </Col>
          );
        })}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const Cover = ({ personalInfo = {} }) => {
  const { advisorInfo, name } = personalInfo;
  const { name: advisorName } = advisorInfo || {};
  const { companyName, companyLogo } = getAdvisorCompanyInfoForDisplay(
    personalInfo
  );

  return (
    <CoverContainer>
      <Image
        // src={companyLogo}
        src={{
          uri: companyLogo,
          method: "GET",
          headers: { "Cache-Control": "no-cache" },
        }}
        style={{
          width: "128px",
          maxHeight: "128px",
          margin: "auto",
          marginBottom: 30,
        }}
      />
      <CoverTitle>{Locale.get("pdfDownload.pdfFar")}</CoverTitle>
      <CoverSection>
        <Text {...ChineseFontStyle} center>
          {Locale.get("pdfDownload.for")}
        </Text>
      </CoverSection>
      <CoverSection>
        <CoverText>{name.toUpperCase()}</CoverText>
      </CoverSection>
      <CoverSection>
        <Text {...ChineseFontStyle} center>
          {Locale.get("pdfDownload.prepareBy")}
        </Text>
      </CoverSection>
      <CoverSection>
        <CoverText>{advisorName.toUpperCase()}</CoverText>
        {companyName ? (
          <Text
            {...ChineseFontStyle}
            center
            width="70%"
            margin="4px auto"
          >{`(${companyName})`}</Text>
        ) : null}
      </CoverSection>
      <CoverSection>
        <Text {...ChineseFontStyle} center>
          {Locale.get("pdfDownload.on")}
        </Text>
      </CoverSection>
      <CoverSection>
        <CoverText>{moment().format("Do MMMM, YYYY")}</CoverText>
      </CoverSection>
    </CoverContainer>
  );
};

const InvestmentResourcesBreakdownTables = ({ investmentResources = [] }) => {
  // Output, to ease table rendering
  // {resourcesLabel: ['Investment', 'Property'], splittedResources: [{}, {}]} // Same length for both array
  // Table result => 2 tables with title from resourcesLabel and content from splittedResources
  const tableData = useMemo(
    () =>
      investmentResources.reduce(
        (tableData, currentInvestmentResource) => {
          if (!currentInvestmentResource.type) {
            return {
              ...tableData,
              resourceLabels: [
                ...tableData.resourceLabels,
                currentInvestmentResource.label,
              ],
              splittedResources: [...tableData.splittedResources, []],
            };
          }

          tableData.splittedResources[
            tableData.splittedResources.length - 1
          ] = [
            ...tableData.splittedResources[
              tableData.splittedResources.length - 1
            ],
            {
              label: currentInvestmentResource.label,
              currentValue: constructDisplayPrice(
                currentInvestmentResource.currentValue,
                "$"
              ),
              returnRate: constructDisplayPercentage(
                currentInvestmentResource.returnRate
              ),
              netValue: constructDisplayPrice(
                currentInvestmentResource.netValue,
                "$"
              ),
            },
          ];

          return tableData;
        },
        { resourceLabels: [], splittedResources: [] }
      ),
    [investmentResources]
  );
  const tableColumns = [
    { key: "label", label: "Resources", widthInPerc: 25 },
    { key: "currentValue", label: "Estimated Current Value", widthInPerc: 30 },
    { key: "netValue", label: "Projected Net Value", widthInPerc: 30 },
    { key: "returnRate", label: "Rate of Return", widthInPerc: 15 },
  ];

  return (
    <>
      {tableData.resourceLabels.length ? (
        tableData.resourceLabels.map((resourceLabel, index) => (
          <TableWithMultipleColumns
            key={resourceLabel}
            title={resourceLabel}
            columns={tableColumns}
            dataSource={tableData.splittedResources[index]}
          />
        ))
      ) : (
        <SectionContainer wrap={false} marginBottom="-1px" bordered>
          <SectionTitleContainer>
            <SectionTitle>{Locale.get("pdfDownload.resource")}</SectionTitle>
          </SectionTitleContainer>
          <SectionContentContainer>
            <Text
              {...ChineseFontStyle}
              // style={{
              //   fontSize: 10,
              //   color: "#949494",
              //   fontStyle: "italic",
              //   textAlign: "center",
              // }}
            >
              {" "}
              {Locale.get("pdfDownload.noResource")}
            </Text>
          </SectionContentContainer>
        </SectionContainer>
      )}
    </>
  );
};

const Footer = ({ adviseeName }) => (
  <>
    <FooterLeft fixed>
      {adviseeName} - {moment().format("DD/MM/YYYY HH:mm")} - powered by Finport
      Tech Sdn Bhd
    </FooterLeft>
    <FooterRight
      render={({ pageNumber, totalPages }) =>
        pageNumber !== 1 && `${pageNumber - 1} / ${totalPages - 1}`
      }
      fixed
    />
  </>
);

const SignOffBox = ({ personalInfo }) => {
  const { advisorInfo } = personalInfo;
  const { name: advisorName } = advisorInfo || {};

  const SignOffItem = ({ label, value, top = 0, bottom = 0 }) => (
    <Row style={{ marginBottom: bottom, marginTop: top }}>
      <Text
        {...ChineseFontStyle}
        style={{ width: 115, fontSize: 9, color: "black" }}
      >
        {label}
      </Text>
      <Text {...ChineseFontStyle} style={{ fontSize: 9 }}>
        {value}
      </Text>
    </Row>
  );

  return (
    <SectionContainer
      bordered
      style={{ padding: 20, alignItems: "center" }}
      wrap={false}
    >
      <Row>
        <Col width="50%">
          <SignOffItem
            //    <Text {...ChineseFontStyle} center>
            //    {Locale.get("pdfDownload.prepareBy")}
            //  </Text>

            label={
              <Text {...ChineseFontStyle}>
                {" "}
                {Locale.get("pdfDownload.reportPreparedBy")}{" "}
              </Text>
            }
            value={advisorName}
            bottom={8}
          />
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {" "}
                {Locale.get("pdfDownload.financialAnalysisDoneBy")}
              </Text>
            }
            value={advisorName}
            bottom={8}
          />
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {Locale.get("pdfDownload.signOffDate")}
              </Text>
            }
            value={
              <Text {...ChineseFontStyle}>
                {moment().format("dddd, MMMM DD, YYYY")}
              </Text>
            }
            bottom={8}
          />
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {Locale.get("pdfDownload.signature")}
              </Text>
            }
            value="___________________"
            top={50}
          />
        </Col>
        <Col width="50%" style={{ paddingLeft: 5 }}>
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {Locale.get("pdfDownload.reportApproveBy")}
              </Text>
            }
            value={advisorName}
            bottom={8}
          />
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {Locale.get("pdfDownload.signOffDate")}
              </Text>
            }
            value={
              <Text {...ChineseFontStyle}>
                {moment().format("dddd, MMMM DD, YYYY")}
              </Text>
            }
            bottom={8}
          />
          <SignOffItem
            label={
              <Text {...ChineseFontStyle}>
                {Locale.get("pdfDownload.signature")}
              </Text>
            }
            value="___________________"
            top={68}
          />
        </Col>
      </Row>
    </SectionContainer>
  );
};

// Create Document Component
const PDFReportDownloadButton = ({
  accidentalDeathCoverageData,
  actionPlans,
  benefitTypes,
  policyStatuses,
  cashFlowSummary,
  comatoseCoverageData,
  estatePlanningDetails,
  financialInfo,
  futureReviews,
  insurancesInfo,
  investmentPortfolio,
  medicalBenefitCoverageData,
  naturalDeathCoverageData,
  personalDetails,
  personalInfo,
  reportingDetails,
  tpdCoverageData,
  contactList,
}) => {
  const [companyLogoLoading, setCompanyLogoLoading] = useState(true);
  const {
    policies = [],
    coveredForPolicies = [],
    linkedFamilyPolicies = [],
    appendixPolicies = [],
    cashFlow = {},
  } = personalDetails;
  const { companyLogo } = getAdvisorCompanyInfoForDisplay(personalInfo);

  useEffect(() => {
    setTimeout(() => {
      loadImage();
    }, 500);
  }, [companyLogo]);

  const loadImage = async () => {
    setCompanyLogoLoading(true);
    cacheImg(companyLogo)
      .then(() => {
        setCompanyLogoLoading(false);
      })
      .catch(() => {
        setCompanyLogoLoading(false);
      });
  };

  const handleOnDownloadReportButtonClick = (url) => {
    message.info(
      "Are you are having issues downloading report? Try switching off Ad Blockers and try again!",
      5
    );
    window.open(url, "_blank");
  };

  const Reporting = () => {
    return (
      <Document>
        <Page size="A4">
          <Cover personalInfo={personalInfo} />
        </Page>
        <Page size="A4">
          <View wrap={false}>
            <PageTitle
              title={Locale.get("pdfDownload.adviseeInfo")}
              index="1.0"
            />
            <InfoBox
              title={Locale.get("pdfDownload.personalDependentInfo")}
              dataSource={constructPersonalInfo(personalInfo)}
              isSideBySide
            />
            <InfoBox
              title={Locale.get("pdfDownload.personalFinInfo")}
              dataSource={constructFinancialInfo(financialInfo)}
            />
          </View>
          <View break>
            <PageTitle title={Locale.get("pdfDownload.cashFlow")} index="2.0" />
            <InfoBox
              title={Locale.get("pdfDownload.cashFlowSummary")}
              dataSource={constructCashFlowSummary(cashFlowSummary)}
            />
            <CashFlow cashFlow={cashFlow} />
          </View>
          <Footer adviseeName={personalInfo.name} />
        </Page>
        {policies.length ? (
          <PolicyTable
            data={policies}
            benefitTypes={benefitTypes}
            personalInfoData={constructPersonalInfo(personalInfo)}
            companyLogo={companyLogo}
            policyStatuses={policyStatuses}
            mode={"personal"}
          />
        ) : null}

        {coveredForPolicies.map((coveredForPolicy, index) => {
          return (
            <PolicyTable
              key={index}
              data={coveredForPolicy.policies}
              benefitTypes={benefitTypes}
              personalInfoData={constructCoveredForPersonalInfo({
                name: coveredForPolicy.coveredFor,
              })}
              companyLogo={companyLogo}
              policyStatuses={policyStatuses}
              mode={"coveredFor"}
            />
          );
        })}

        {linkedFamilyPolicies.map((linkedFamilyPolicy, index) => {
          return (
            <PolicyTable
              key={index}
              data={linkedFamilyPolicy.policies}
              benefitTypes={benefitTypes}
              personalInfoData={constructPersonalInfo(
                linkedFamilyPolicy.personalInfo
              )}
              companyLogo={companyLogo}
              policyStatuses={policyStatuses}
              mode={"linkedFamilyMember"}
            />
          );
        })}

        <Page size="A4">
          <View>
            <PageTitle
              title={Locale.get("pdfDownload.insuranceSummary")}
              index="3.0"
            />
            <InfoBox
              title={Locale.get("pdfDownload.planCoverage")}
              dataSource={constructInsuranceInfo(insurancesInfo).filter(
                (ins) => ins.category === "planCoverage"
              )}
            />
            <InfoBox
              title={Locale.get("pdfDownload.hospitalizationBenefit")}
              dataSource={constructInsuranceInfo(insurancesInfo).filter(
                (ins) => ins.category === "hospitalization"
              )}
            />
            <InfoBox
              title={Locale.get("pdfDownload.medCard")}
              dataSource={constructInsuranceInfo(insurancesInfo).filter(
                (ins) => ins.category === "medicalCard"
              )}
            />
            <InfoBox
              title={Locale.get("pdfDownload.total")}
              dataSource={constructInsuranceInfo(insurancesInfo).filter(
                (ins) => !ins.category
              )}
            />
          </View>
          <View break>
            <PageTitle
              title={Locale.get("pdfDownload.grossInsuranceNeed")}
              index="4.0"
            />
            <InfoBox
              title={Locale.get("pdfDownload.assumption")}
              dataSource={constructAssumptionInfo(
                reportingDetails.grossInsNeeds &&
                  reportingDetails.grossInsNeeds.deathCover
              )}
            />
            <InfoBox
              title={Locale.get(
                "pdfDownload.deathCoverWithoutSettlingMortgage"
              )}
              dataSource={constructGrossInsNeedsDataSource(
                "Death Coverage",
                reportingDetails.grossInsNeeds &&
                  reportingDetails.grossInsNeeds.deathCover
              )}
              marginBottom="-1px"
            />
            <InfoBox
              title={Locale.get("pdfDownload.totalPermanentDisability")}
              dataSource={constructGrossInsNeedsDataSource(
                "TPD Coverage",
                reportingDetails.grossInsNeeds &&
                  reportingDetails.grossInsNeeds.tpd
              )}
              marginBottom="-1px"
            />
            <InfoBox
              title={Locale.get("pdfDownload.criticalIllnessAllExpense")}
              dataSource={constructGrossInsNeedsDataSource(
                "CI Coverage",
                reportingDetails.grossInsNeeds &&
                  reportingDetails.grossInsNeeds.ci
              )}
            />
          </View>
          <View break>
            <PageTitle
              title={Locale.get("pdfDownload.riskRatio")}
              index="5.0"
            />
            <RiskRatio
              title={Locale.get("pdfDownload.naturalDeathCoverage")}
              dataSource={naturalDeathCoverageData}
            />
            <RiskRatio
              title={Locale.get("pdfDownload.accidentalDeathCover")}
              dataSource={accidentalDeathCoverageData}
            />
          </View>
          <View break>
            <RiskRatio
              title={Locale.get("pdfDownload.criticalIllnessMedBenefitCover")}
              dataSource={medicalBenefitCoverageData}
            />
            <RiskRatio
              title={Locale.get("pdfDownload.tpdCoverage")}
              dataSource={tpdCoverageData}
            />
            <RiskRatio
              title={Locale.get("pdfDownload.comatoseCoverage")}
              dataSource={comatoseCoverageData}
            />
          </View>
          <View break>
            <PageTitle
              title={Locale.get("pdfDownload.estatePlanning")}
              index="6.0"
            />
            <TableWithMultipleColumns
              title={Locale.get("pdfDownload.personalEstateContingencyPlan")}
              columns={[
                {
                  key: "plan",
                  label: Locale.get("pdfDownload.estatePlanning"),
                  widthInPerc: 30,
                },
                {
                  key: "interpretation",
                  label: Locale.get("pdfDownload.interpretation"),
                  widthInPerc: 20,
                },
                {
                  key: "remarks",
                  label: Locale.get("pdfDownload.note_a"),
                  widthInPerc: 50,
                },
              ]}
              dataSource={estatePlanningDetails.estatePlannings}
            />
            <TableWithMultipleColumns
              title={Locale.get("pdfDownload.expressEmergencyContingencyPlan")}
              columns={[
                {
                  key: "plan",
                  label: Locale.get("pdfDownload.event"),
                  widthInPerc: 30,
                },
                {
                  key: "interpretation",
                  label: Locale.get("pdfDownload.interpretation"),
                  widthInPerc: 20,
                },
                {
                  key: "remarks",
                  label: Locale.get("pdfDownload.note_b"),
                  widthInPerc: 50,
                },
              ]}
              dataSource={estatePlanningDetails.emergencyContingencyPlannings}
            />
            <TableWithMultipleColumns
              title={Locale.get("pdfDownload.fundContingencyPlan")}
              columns={[
                {
                  key: "event",
                  label: Locale.get("pdfDownload.event"),
                  widthInPerc: 30,
                },
                {
                  key: "solution",
                  label: Locale.get("pdfDownload.solution"),
                  widthInPerc: 20,
                },
                {
                  key: "toDateStatus",
                  label: Locale.get("pdfDownload.toDateStatus"),
                  widthInPerc: 25,
                },
                {
                  key: "remarks",
                  label: Locale.get("pdfDownload.detail"),
                  widthInPerc: 25,
                },
              ]}
              dataSource={estatePlanningDetails.contingencyPlanningFundings}
            />
          </View>
          <View wrap break>
            <PageTitle
              title={Locale.get("pdfDownload.investmentPortfolio")}
              index="7.0"
            />
            <InfoBox
              title={Locale.get("pdfDownload.goal")}
              dataSource={constructInvestmentGoal(
                investmentPortfolio.goalSetting
              )}
            />
            <InfoBox
              title={Locale.get("pdfDownload.assumption")}
              dataSource={constructInvestmentAssumption(
                investmentPortfolio.assumptionInfo
              )}
              isSideBySide
            />
            <InfoBox
              title={Locale.get("pdfDownload.summary")}
              dataSource={constructInvestmentSummaryInfo(
                investmentPortfolio.summaryInfo
              )}
            />
            <InvestmentResourcesBreakdownTables
              investmentResources={investmentPortfolio.resources}
            />
          </View>
          <View wrap break>
            <View>
              <PageTitle
                title={Locale.get("pdfDownload.actionPlan")}
                index="8.0"
              />
              <TableWithMultipleColumns
                columns={[
                  {
                    key: "area",
                    label: Locale.get("pdfDownload.area"),
                    widthInPerc: 20,
                  },
                  {
                    key: "action",
                    label: Locale.get("pdfDownload.suggestedAction"),
                    widthInPerc: 25,
                  },
                  {
                    key: "initiator",
                    label: Locale.get("pdfDownload.initiator"),
                    widthInPerc: 15,
                  },
                  {
                    key: "actionDate",
                    label: Locale.get("pdfDownload.actionDate"),
                    widthInPerc: 15,
                  },
                  {
                    key: "decision",
                    label: Locale.get("pdfDownload.decision"),
                    widthInPerc: 25,
                  },
                ]}
                dataSource={actionPlans}
              />
            </View>
            <View style={{ marginTop: 20, marginBottom: 20 }} wrap={false}>
              <PageTitle
                title={Locale.get("pdfDownload.futureAreaVenture")}
                index="9.0"
              />
              <TableWithMultipleColumns
                columns={[
                  {
                    key: "area",
                    label: Locale.get("pdfDownload.areaFutureReview"),
                    widthInPerc: 30,
                  },
                  {
                    key: "problem",
                    label: Locale.get("pdfDownload.problemToSolve"),
                    widthInPerc: 50,
                  },
                  {
                    key: "nextReviewDate",
                    label: Locale.get("pdfDownload.nextReviewDate"),
                    widthInPerc: 20,
                  },
                ]}
                dataSource={futureReviews}
              />
            </View>
            <SignOffBox personalInfo={personalInfo} />
          </View>

          <Footer adviseeName={personalInfo.name} />
        </Page>

        {appendixPolicies.map((policy, idx) => (
          <Page key={idx}>
            <View>
              <Policy
                key={idx}
                policy={policy}
                index={idx + 1}
                personalDetails={personalDetails}
              />
            </View>
            <Footer adviseeName={personalInfo.name} />
          </Page>
        ))}

        <Page size="A4">
          <EmergencyContact policies={policies} contactList={contactList} />
          <Footer adviseeName={personalInfo.name} />
        </Page>
      </Document>
    );
  };

  // Font.register({
  //   family: "MicrosoftBlack",
  //   src: "/MicrosoftBlack.ttf",
  // });
  // Font.registerHyphenationCallback((word) => [word]);

  const downloadFilename = `${moment().format(
    "YYYYMMDD"
  )}_${personalInfo.name.replace(/\s+/g, "-")}_FAR.pdf`;

  const [pdfloading, setPdfLoading] = useState(true);

  const registerfont = () => {
    // const cookie = new Cookies();

    // console.log(cookie.get("localization"));

    // if (cookie.get("localization") === "zh-CN") {
    Font.register({
      family: "ChineseFont",
      src: "/MicrosoftBlack.ttf",
      // src: "/Noto_Sans_TC/static/NotoSansTC-Black.ttf",
    });
    // }

    if (navigator.connection) {
      console.log(
        `Effective network type: ${navigator.connection.effectiveType}`
      );
      console.log(`Downlink Speed: ${navigator.connection.downlink}Mb/s`);
      console.log(`Round Trip Time: ${navigator.connection.rtt}ms`);
    } else {
      console.log("Navigator Connection API not supported");
    }

    setTimeout(() => {
      setPdfLoading(false);
    }, 3000);
  };

  useEffect(() => {
    registerfont();
  }, []);

  return (
    <BlobProvider document={!companyLogoLoading && Reporting()}>
      {({ url, loading }) => (
        <a
          href={url}
          download={downloadFilename}
          style={{ color: "white", marginLeft: 8 }}
        >
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={loading || companyLogoLoading}
            onClick={() => handleOnDownloadReportButtonClick(url)}
          >
            {loading
              ? Locale.get("reporting.loadingButton")
              : Locale.get("reporting.downloadButton")}
            <div
              style={{
                height: 1,
                width: 1,
                backgroundColor: "#fff",
                marginTop: 0,
                marginLeft: 0,
              }}
            >
              <img
                src={companyLogo}
                style={{
                  width: "1px",
                  maxHeight: "1px",
                  margin: "auto",
                  marginBottom: 0,
                }}
              />
            </div>
          </Button>
        </a>
      )}
    </BlobProvider>
  );
};

export default PDFReportDownloadButton;
